// DEPRECATED-LEANING (QA note): this v0.9 dry-run adapter interface predates the canonical
// 12F ProIntegrationDryRunAdapterService + 25R AdapterMappingEditorService in @djsem/render-state.
// New integration work goes THERE. This file remains for the v0.9 rehearsal test surface; its
// hardware kind names (artnet/sacn/dmx) are taxonomy labels structurally forced to emittedToHardware:false.
// @djsem/live-core — rehearsal profile + pro-output adapter DRY-RUN boundary.
//
// Adapters CONSUME internal events and never own show meaning (rule 8); real hardware
// protocols are out of scope (rule 3) — this file defines the contract seam only.
// dryRun() produces logs, never output. Safe mode / blackout always outrank adapters
// (rule 9): the conductor applies safety BEFORE events ever reach an adapter, and the
// dry-run result must echo the safety mode it saw so audits can prove it.
import {
  vArray, vBoolean, vId, vNumber, vObject, vString, vUnit,
  type Validator,
} from "@djsem/shared";

// ---- rehearsal profile --------------------------------------------------------

/** How a rehearsal session drives its timeline. */
export type RehearsalMode = "simulate_timeline" | "replay_jsonl" | "manual_scrub";
/** Configuration for a rehearsal run: mode, start position, speed, looping, and safety defaults. */
export interface RehearsalProfile {
  id: string;
  label: string;
  mode: RehearsalMode;
  startAtMs: number;
  speed: number;              // 1 = realtime
  loop: boolean;
  safeModeOnStart: boolean;   // rehearsal defaults to safe until the operator arms output
  notes?: string;
}
/** Validator for {@link RehearsalProfile}. */
export const vRehearsalProfile: Validator<RehearsalProfile> = vObject(
  {
    id: vId(), label: vString({ min: 1 }),
    mode: vString({ enum: ["simulate_timeline", "replay_jsonl", "manual_scrub"] }),
    startAtMs: vNumber({ min: 0 }), speed: vNumber({ min: 0.1, max: 8 }),
    loop: vBoolean, safeModeOnStart: vBoolean,
  },
  { notes: vString({}) },
) as Validator<RehearsalProfile>;

// ---- adapter dry-run boundary ---------------------------------------------------

/**
 * Adapter output-target taxonomy label for a {@link ProOutputAdapter}. Hardware-sounding
 * names (artnet/sacn/dmx) are taxonomy labels only — every adapter here is structurally
 * forced to dry-run (`emittedToHardware: false`); no real hardware protocol is implemented.
 */
export type ProAdapterType =
  | "osc" | "websocket" | "midi" | "touchdesigner" | "resolume" | "madmapper"
  | "artnet" | "sacn" | "dmx" | "laser_safe";
/** Subscription plan tier required to use a given {@link ProOutputAdapter}. */
export type ProAdapterPlanLevel = "pro_integrations" | "venue" | "enterprise";

/** Result of validating a {@link ProOutputAdapter} configuration. */
export interface AdapterValidationResult { valid: boolean; issues: string[] }
/** One logged line of a {@link ProOutputAdapter.dryRun} result. */
export interface AdapterDryRunLogEntry { timeMs: number; eventKind: string; mappedSummary: string }
/** Outcome of running a {@link ProOutputAdapter} in dry-run mode: never real hardware output. */
export interface AdapterDryRunResult {
  adapterId: string;
  adapterType: ProAdapterType;
  eventsSeen: number;
  eventsMapped: number;
  safetyModeSeen: "normal" | "safe_idle" | "blackout" | "emergency";
  emittedToHardware: false;   // structurally impossible to claim real output from a dry run
  log: AdapterDryRunLogEntry[];
  warnings: string[];
}

/**
 * The pro-output adapter contract. Implementations live behind the Pro Integrations
 * boundary and are DRY-RUN ONLY in this build phase — they consume internal DJSEM events
 * and never own show meaning. `TEvent` is the adapter's own mapped output shape.
 */
export interface ProOutputAdapter<TConfig, TEvent, TInternalEvent = Record<string, unknown>> {
  readonly id: string;
  readonly label: string;
  readonly adapterType: ProAdapterType;
  readonly planLevel: ProAdapterPlanLevel;
  validateConfig(config: TConfig): AdapterValidationResult;
  mapEvent(event: TInternalEvent, config: TConfig): TEvent[];
  dryRun(events: TInternalEvent[], config: TConfig): AdapterDryRunResult;
}

/** All recognized {@link ProAdapterType} values. */
export const ADAPTER_TYPES: readonly ProAdapterType[] = [
  "osc", "websocket", "midi", "touchdesigner", "resolume", "madmapper", "artnet", "sacn", "dmx", "laser_safe",
] as const;

/** Validator for {@link AdapterDryRunResult}; also enforces `emittedToHardware: false`. */
export const vAdapterDryRunResult: Validator<AdapterDryRunResult> = vObject(
  {
    adapterId: vId(),
    adapterType: vString({ enum: ADAPTER_TYPES as string[] }),
    eventsSeen: vNumber({ min: 0, int: true }),
    eventsMapped: vNumber({ min: 0, int: true }),
    safetyModeSeen: vString({ enum: ["normal", "safe_idle", "blackout", "emergency"] }),
    emittedToHardware: (v, path = "$") => (v === false ? { valid: true, issues: [] } : { valid: false, issues: [{ path, message: "dry runs must declare emittedToHardware: false" }] }),
    log: vArray(vObject({ timeMs: vNumber({ min: 0 }), eventKind: vString({ min: 1 }), mappedSummary: vString({ min: 1 }) })),
    warnings: vArray(vString({})),
  },
) as Validator<AdapterDryRunResult>;
